/**
 * Labels editor component.
 *
 * Provides a compact UI for editing a string-to-string label dictionary.
 * Supports marking values as secrets (displayed as ****).
 *
 * @module components/ui/LabelsEditor
 */

import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { Plus, X, Eye, EyeOff } from 'lucide-react';
import { Input } from './Input';
import { Button } from './Button';

/**
 * Ref handle for LabelsEditor component.
 * Allows parent components to access uncommitted draft values.
 */
export interface LabelsEditorRef {
  /** Returns the current draft key/value if valid, or null if not */
  getDraft: () => { key: string; value: string } | null;
  /** Commits the current draft if valid, returns true if successful */
  commitDraft: () => boolean;
}

export interface LabelsEditorProps {
  /** Current labels value */
  value: Record<string, string>;
  /** Called when the labels dictionary changes */
  onChange: (next: Record<string, string>) => void;

  /** Optional heading label */
  label?: string;
  /** Optional helper/description text */
  helperText?: string;

  /** Disable editing */
  disabled?: boolean;

  /** Placeholder for the key input */
  keyPlaceholder?: string;
  /** Placeholder for the value input */
  valuePlaceholder?: string;

  /** If true, require a value to be provided before adding (default: true) */
  requireValue?: boolean;

  /**
   * How to handle duplicate keys:
   * - 'overwrite': replace existing value
   * - 'prevent': do not add and show an error
   */
  duplicateKeyBehavior?: 'overwrite' | 'prevent';

  /** If true, show a checkbox to mark values as secrets (default: false) */
  supportSecrets?: boolean;

  /** Set of keys that are marked as secrets (controlled from parent) */
  secretKeys?: Set<string>;

  /** Called when secret keys change */
  onSecretKeysChange?: (secretKeys: Set<string>) => void;
}

/**
 * A small labels editor (key/value add, chips list, remove).
 */
export const LabelsEditor = forwardRef<LabelsEditorRef, LabelsEditorProps>(function LabelsEditor(
  {
    value,
    onChange,
    label = 'Labels (Optional)',
    helperText,
    disabled = false,
    keyPlaceholder = 'Key',
    valuePlaceholder = 'Value',
    requireValue = true,
    duplicateKeyBehavior = 'overwrite',
    supportSecrets = false,
    secretKeys: externalSecretKeys,
    onSecretKeysChange,
  },
  ref
) {
  const [draftKey, setDraftKey] = useState('');
  const [draftValue, setDraftValue] = useState('');
  const [draftIsSecret, setDraftIsSecret] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track which secrets are revealed (for the eye toggle)
  const [revealedSecrets, setRevealedSecrets] = useState<Set<string>>(new Set());
  // Internal secret keys state (used when not controlled externally)
  const [internalSecretKeys, setInternalSecretKeys] = useState<Set<string>>(new Set());

  // Use external secret keys if provided, otherwise use internal state
  const secretKeys = externalSecretKeys ?? internalSecretKeys;
  const setSecretKeys = (newSecretKeys: Set<string>) => {
    if (onSecretKeysChange) {
      onSecretKeysChange(newSecretKeys);
    } else {
      setInternalSecretKeys(newSecretKeys);
    }
  };

  const entries = useMemo(() => Object.entries(value), [value]);

  const canAdd = useMemo(() => {
    const k = draftKey.trim();
    const v = draftValue.trim();
    if (!k) return false;
    if (requireValue && !v) return false;
    return true;
  }, [draftKey, draftValue, requireValue]);

  // Expose getDraft and commitDraft methods via ref
  useImperativeHandle(ref, () => ({
    getDraft: () => {
      const k = draftKey.trim();
      const v = draftValue.trim();
      if (!k) return null;
      if (requireValue && !v) return null;
      return { key: k, value: v };
    },
    commitDraft: () => {
      if (canAdd) {
        addLabel();
        return true;
      }
      return false;
    },
  }));

  const addLabel = () => {
    if (disabled) return;

    const k = draftKey.trim();
    const v = draftValue.trim();

    if (!k) {
      setError('Key is required');
      return;
    }

    if (requireValue && !v) {
      setError('Value is required');
      return;
    }

    const alreadyExists = Object.prototype.hasOwnProperty.call(value, k);
    if (alreadyExists && duplicateKeyBehavior === 'prevent') {
      setError(`Label key "${k}" already exists`);
      return;
    }

    setError(null);
    onChange({ ...value, [k]: v });

    // Handle secret key tracking
    if (supportSecrets && draftIsSecret) {
      const newSecretKeys = new Set(secretKeys);
      newSecretKeys.add(k);
      setSecretKeys(newSecretKeys);
    }

    setDraftKey('');
    setDraftValue('');
    setDraftIsSecret(false);
  };

  const removeLabel = (key: string) => {
    if (disabled) return;
    const next = { ...value };
    delete next[key];
    onChange(next);

    // Remove from secret keys if it was a secret
    if (secretKeys.has(key)) {
      const newSecretKeys = new Set(secretKeys);
      newSecretKeys.delete(key);
      setSecretKeys(newSecretKeys);
    }

    // Remove from revealed secrets
    if (revealedSecrets.has(key)) {
      const newRevealed = new Set(revealedSecrets);
      newRevealed.delete(key);
      setRevealedSecrets(newRevealed);
    }
  };

  const toggleReveal = (key: string) => {
    const newRevealed = new Set(revealedSecrets);
    if (newRevealed.has(key)) {
      newRevealed.delete(key);
    } else {
      newRevealed.add(key);
    }
    setRevealedSecrets(newRevealed);
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</div>
        {helperText && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
        )}
      </div>

      {entries.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {entries.map(([k, v]) => {
            const isSecret = secretKeys.has(k);
            const isRevealed = revealedSecrets.has(k);
            const displayValue = isSecret && !isRevealed ? '••••••••' : v;

            return (
              <span
                key={k}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200"
              >
                {k}: {displayValue}
                {isSecret && (
                  <button
                    type="button"
                    onClick={() => toggleReveal(k)}
                    className="hover:text-primary-900 dark:hover:text-primary-100 ml-1"
                    disabled={disabled}
                    aria-label={isRevealed ? 'Hide value' : 'Show value'}
                  >
                    {isRevealed ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeLabel(k)}
                  className="hover:text-primary-900 dark:hover:text-primary-100"
                  disabled={disabled}
                  aria-label={`Remove label ${k}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="flex gap-2 items-center">
        <Input
          placeholder={keyPlaceholder}
          value={draftKey}
          onChange={(e) => {
            setDraftKey(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (canAdd) addLabel();
            }
          }}
          disabled={disabled}
        />
        <Input
          type={supportSecrets && draftIsSecret ? 'password' : 'text'}
          placeholder={valuePlaceholder}
          value={draftValue}
          onChange={(e) => {
            setDraftValue(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (canAdd) addLabel();
            }
          }}
          disabled={disabled}
        />
        {supportSecrets && (
          <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap cursor-pointer">
            <input
              type="checkbox"
              checked={draftIsSecret}
              onChange={(e) => setDraftIsSecret(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              disabled={disabled}
            />
            Secret
          </label>
        )}
        <Button
          type="button"
          variant="secondary"
          onClick={addLabel}
          disabled={disabled || !canAdd}
          aria-label="Add label"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {error && <p className="text-sm text-danger-500 dark:text-danger-400">{error}</p>}
    </div>
  );
});
